import { Box, Button, Container, Grid, Tooltip, Typography } from "@mui/material"
import { GridRenderCellParams, GridValueGetterParams } from "@mui/x-data-grid"
import { useMemo } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"
import DataGrid from "../components/common/DataGrid"
import { dateToString } from "../helpers/date.helpers"
import {
    getLicenseDate,
    getLicenseDateLabel,
    getUserLicenseStateOfUser,
    UserLicenseType,
} from "../helpers/license.helpers"
import { useAppDispatch, useAppSelector } from "../hooks/useRedux"
import { UserT } from "../store/slices/user.slice"
import { getUsersAsync, verifyUsersAsync } from "../store/slices/users.slice"
import { isAdmin } from "../hooks/useSession"

export default function AdminUsers() {
    const { t } = useTranslation()

    const navigate = useNavigate()
    const dispatch = useAppDispatch()
    const users = useAppSelector((state) => state.users)

    const usersAdminColumns = useMemo(() => {
        const handleVerify = (id: string, isActive: boolean) => {
            dispatch(verifyUsersAsync(id, isActive))
        }
        return [
            {
                field: "email",
                headerName: t("user:userLicenseDataGrid.header.email"),
                minWidth: 200,
                valueGetter: ({ row }: GridValueGetterParams) => {
                    return row.email
                },
            },
            {
                field: "license",
                sortable: false,
                headerName: t("user:userLicenseDataGrid.header.license"),
                valueGetter: ({ row }: GridValueGetterParams) => {
                    const userLicenseStatus = getUserLicenseStateOfUser(row as UserT)
                    return isAdmin(row.roles)
                        ? t("license:superAdmin")
                        : [
                              UserLicenseType.Free,
                              UserLicenseType.UnlimitedPlanExpiredToFree,
                              UserLicenseType.UnlimitedPlanRevoked,
                          ].includes(userLicenseStatus.type as UserLicenseType)
                        ? t("license:free")
                        : t("license:enterprise")
                },
            },
            {
                field: "period",
                sortable: false,
                headerName: t("user:userLicenseDataGrid.header.period"),
                valueGetter: ({ row }: GridValueGetterParams) => {
                    return isAdmin(row.roles)
                        ? t("license:period.limitless")
                        : `${
                              !row.license?.period
                                  ? t("license:period.monthly")
                                  : row.license?.period === 12
                                  ? t("license:period.yearly")
                                  : t("license:period.monthly")
                          }`
                },
            },
            {
                field: "status",
                sortable: false,
                headerName: t("user:userLicenseDataGrid.header.status"),
                minWidth: 250,
                valueGetter: ({ row }: GridValueGetterParams) => {
                    const userLicenseStatus = getUserLicenseStateOfUser(row as UserT)

                    return isAdmin(row.roles)
                        ? t("user:userLicenseDataGrid.active")
                        : t(`user:userLicenseDataGrid.state.${userLicenseStatus.type}`)
                },
            },
            {
                field: "date",
                sortable: false,
                headerName: t("user:userLicenseDataGrid.header.licenseDate"),
                minWidth: 160,
                valueGetter: ({ row }: GridValueGetterParams) => {
                    const licenseDate = getLicenseDate(getUserLicenseStateOfUser(row as UserT))

                    return licenseDate ? dateToString(licenseDate) : "-"
                },
                renderCell: ({ row, value }: GridRenderCellParams) => {
                    const label = getLicenseDateLabel(getUserLicenseStateOfUser(row as UserT))

                    return (
                        <Box>
                            <Typography variant="body2" lineHeight={1.4}>
                                {value}
                            </Typography>
                            {label && (
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    lineHeight={1.4}
                                    display="block"
                                >
                                    {t(`user:userLicenseDataGrid.dateLabel.${label}`)}
                                </Typography>
                            )}
                        </Box>
                    )
                },
            },
            {
                field: "createdAt",
                headerName: t("user:userLicenseDataGrid.header.signupDate"),
                minWidth: 180,
                type: "date",
                valueGetter: ({ row }: GridValueGetterParams) => {
                    return dateToString(row.createdAt)
                },
            },
            {
                field: "verifyButton",
                sortable: false,
                disableColumnMenu: true,
                headerName: t("user:userLicenseDataGrid.header.action"),
                minWidth: 120,
                renderCell: ({ row }: GridRenderCellParams) => {
                    const isActive =
                        row.license?.expired === null ||
                        (row.license?.expired && row.license?.expired.getTime() > Date.now())

                    return (
                        <>
                            <Tooltip placement={"right"} title={t("user:button:upgradeTooltip")}>
                                <span>
                                    <Button
                                        color={isActive ? "error" : "success"}
                                        onClick={() => handleVerify(row._id, !isActive)}
                                    >
                                        {isActive
                                            ? t("user:button.revoke")
                                            : t("user:button.approve")}
                                    </Button>
                                </span>
                            </Tooltip>
                        </>
                    )
                },
            },
        ]
    }, [dispatch, t])

    const handleNavigateToProfile = () => {
        navigate("/account/profile")
    }

    return (
        <Container component="main" maxWidth="lg">
            <Grid container p={0} my={4} width={"100%"} justifyContent="space-between">
                <Grid item md={12} xs={12} mb={1}>
                    <Typography variant="h2">{t("user:usersAdmin")}</Typography>

                    <Button variant="text" onClick={handleNavigateToProfile}>
                        {t("user:button.backToProfile")}
                    </Button>
                </Grid>
                <Grid item md={12} py={2} sx={{ borderBottom: 1 }}></Grid>
                <Grid item md={12} mt={2}>
                    <DataGrid
                        data={users.data}
                        total={users.total}
                        onChange={(queryOptions) => dispatch(getUsersAsync(queryOptions))}
                        columns={usersAdminColumns}
                        loading={!users.status}
                    />
                </Grid>
            </Grid>
        </Container>
    )
}
